import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EmployeeCustomerServiceWarranty } from '../../interfaces/EmployeeInterface';
import {
  PayrollCswTaskBody, PayrollEntryResult,
} from '../../interfaces/TimesheetEditInterface';
import { CustomerServiceWarrantyOption } from '../../api/customerServiceWarranty';
import { formatMinutesAs } from '../../util/hoursFormat';
import { parseAmt, parseDur, ymdLabel } from './clockParse';
import { getCustomerServiceWarrantiesCached } from './lookups';
import ProjectPicker, { PickedProject } from './ProjectPicker';
import { EntryErrorBanner, EntryModalShell, performEntrySave } from './EntryModalShell';
import { AmountEchoParts, DurEchoParts } from './echoParts';

/**
 * CSW claim editor (M2-B). Warranty picker (admin CSW list), minutes as a
 * 10-key duration field, amount, optional description. A warranty flagged
 * `projectRequired` forces a project pick; otherwise the project rides as the
 * contract's 0 sentinel (§8.5 `requireInt`).
 */

export interface CswCarry {
  cswID: number | null;
  project: PickedProject | null;
}

export interface CswTaskModalProps {
  employeeID: number;
  payPeriodID: number;
  ymd: string;
  employeeName: string;
  editSessionID: string;
  entry: EmployeeCustomerServiceWarranty | null;
  carry: CswCarry | null;
  onSaved: (result: PayrollEntryResult, close: boolean, carry: CswCarry) => void;
  onCancel: () => void;
  onConflict: (message: string) => void;
}

const CswTaskModal: React.FC<CswTaskModalProps> = ({
  employeeID, payPeriodID, ymd, employeeName, editSessionID,
  entry, carry, onSaved, onCancel, onConflict,
}) => {
  const isEdit = entry != null;
  const [options, setOptions] = useState<CustomerServiceWarrantyOption[]>([]);
  const [cswID, setCswID] = useState<number | null>(
    isEdit ? entry!.customerServiceWarrantyID : carry?.cswID ?? null
  );
  const [project, setProject] = useState<PickedProject | null>(
    isEdit
      ? (entry!.project && entry!.project.projectID != null
        ? { projectID: entry!.project.projectID, altProjectID: entry!.project.altProjectID ?? '', name: entry!.project.name ?? '' }
        : null)
      : carry?.project ?? null
  );
  // Prefill as H:MM — parseDur round-trips it exactly.
  const [minsRaw, setMinsRaw] = useState<string>(
    isEdit ? formatMinutesAs(entry!.minutesClaimed ?? 0, 'hhmm') : ''
  );
  const [amountRaw, setAmountRaw] = useState<string>(isEdit ? String(entry!.amountClaimed ?? '') : '');
  const [desc, setDesc] = useState<string>(isEdit ? entry!.description ?? '' : '');
  const [bad, setBad] = useState<'mins' | 'amount' | null>(null);
  const [banner, setBanner] = useState<{ title: string; body?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const minsRef = useRef<HTMLInputElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getCustomerServiceWarrantiesCached()
      .then((rows) => {
        setOptions(rows);
        setCswID((current) => current ?? rows[0]?.customerServiceWarrantyID ?? null);
      })
      .catch(() => setOptions([]));
  }, []);

  const selected = useMemo(
    () => options.find((o) => o.customerServiceWarrantyID === cswID) ?? null,
    [options, cswID]
  );
  const projectRequired = selected?.projectRequired === true;

  const handleSave = async (close: boolean) => {
    if (cswID == null) {
      setBanner({ title: 'Pick a warranty first.', body: 'The claim must name a customer service warranty.' });
      return;
    }
    const mins = parseDur(minsRaw);
    if (mins == null || mins < 0) { setBad('mins'); minsRef.current?.focus(); return; }
    const amount = amountRaw.trim() === '' ? 0 : parseAmt(amountRaw);
    if (amount == null || amount < 0) { setBad('amount'); amountRef.current?.focus(); return; }
    if (projectRequired && project == null) {
      setBanner({ title: 'This warranty requires a project.', body: `${selected?.name ?? 'The warranty'} is project-required — pick the job it belongs to.` });
      return;
    }

    const body: PayrollCswTaskBody = {
      ...(isEdit ? {} : { employeeID, payPeriodID, entryDate: ymd }),
      customerServiceWarrantyID: cswID,
      projectID: project?.projectID ?? 0,
      minutesClaimed: mins,
      amountClaimed: amount,
      locationName: isEdit ? entry!.locationName ?? '' : '',
      description: desc,
      editSessionID,
    };

    setSaving(true);
    setBanner(null);
    const outcome = await performEntrySave(
      'cswTask',
      isEdit ? entry!.employeeCustomerServiceWarrantyID : null,
      body
    );
    setSaving(false);
    if (!outcome.ok) {
      if (outcome.conflict) onConflict(outcome.message);
      else setBanner({ title: 'Could not save the CSW claim.', body: outcome.message });
      return;
    }
    onSaved(outcome.result, close, { cswID, project });
  };

  return (
    <EntryModalShell
      kind="cswTask"
      mode={isEdit ? 'edit' : 'add'}
      subtitle={`${ymdLabel(ymd)} · ${employeeName}`}
      banner={banner ? <EntryErrorBanner title={banner.title} body={banner.body} /> : null}
      echo={(
        <>
          <DurEchoParts raw={minsRaw} />
          <AmountEchoParts raw={amountRaw} />
        </>
      )}
      saving={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="field">
        <label>Warranty</label>
        <select
          className="pe-in sans"
          value={cswID ?? ''}
          onChange={(ev) => setCswID(ev.target.value === '' ? null : Number(ev.target.value))}
        >
          <option value="">{options.length ? 'Select warranty…' : 'Loading warranties…'}</option>
          {options.map((o) => (
            <option key={o.customerServiceWarrantyID} value={o.customerServiceWarrantyID}>
              {o.name}{o.projectRequired ? ' (project required)' : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Project{projectRequired ? '' : ' (optional)'}</label>
        <ProjectPicker
          value={project}
          onChange={setProject}
          allowNone={!projectRequired}
          noneLabel="No project"
          placeholder="Search projects…"
        />
      </div>
      <div className="row2">
        <div className="field">
          <label>Time claimed</label>
          <input
            ref={minsRef}
            className={`pe-in${bad === 'mins' ? ' bad' : ''}`}
            value={minsRaw}
            onChange={(ev) => { setMinsRaw(ev.target.value); if (bad === 'mins') setBad(null); }}
            placeholder="1:30 or 90"
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label>Amount claimed</label>
          <input
            ref={amountRef}
            className={`pe-in${bad === 'amount' ? ' bad' : ''}`}
            value={amountRaw}
            onChange={(ev) => { setAmountRaw(ev.target.value); if (bad === 'amount') setBad(null); }}
            placeholder="0.00"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <div className="field">
        <label>Description (optional)</label>
        <input
          className="pe-in sans"
          value={desc}
          onChange={(ev) => setDesc(ev.target.value)}
          placeholder="Punch list, ceiling patch"
          autoComplete="off"
        />
      </div>
    </EntryModalShell>
  );
};

export default CswTaskModal;
